export interface ManifestTicket { id: string; issuance: string; name: string; seat: string; token: string; boarded: boolean }
export interface Manifest {
  id: string; device: string; departure_id: number; departure: string; organisation_code: string; organisation_name: string;
  route: { name: string }; vehicle: { registration: string }; created_at: string; valid_until: string; tickets: ManifestTicket[];
}
export interface Envelope { payload: string; signature: string; public_key: string; manifest: Manifest }
export interface ScanEvent { id: string; sequence: number; token: string; scanned_at: string; result?: { status: string; reason?: string } }
export interface TerminalState { envelope: Envelope; deviceSecret: string; events: ScanEvent[]; accepted: string[]; synced: number }
const bytes = (value: string) => Uint8Array.from(atob(value), char => char.charCodeAt(0));
const base64 = (value: ArrayBuffer | Uint8Array) => btoa(Array.from(new Uint8Array(value instanceof Uint8Array ? value.buffer as ArrayBuffer : value), char => String.fromCharCode(char)).join(""));

export async function verifyEnvelope(envelope: Envelope): Promise<Manifest> {
  const key = await crypto.subtle.importKey("raw", bytes(envelope.public_key), "Ed25519", false, ["verify"]);
  if (!await crypto.subtle.verify("Ed25519", key, bytes(envelope.signature), bytes(envelope.payload))) throw new Error("La signature du manifeste est invalide.");
  const manifest = JSON.parse(new TextDecoder().decode(bytes(envelope.payload))) as Manifest;
  if (!manifest.id || !manifest.device || !manifest.organisation_code || !Array.isArray(manifest.tickets) || !Number.isFinite(Date.parse(manifest.valid_until))) throw new Error("Manifeste incomplet.");
  return manifest;
}

async function storageKey(pin: string, salt: Uint8Array) {
  const material = await crypto.subtle.importKey("raw", new TextEncoder().encode(pin), "PBKDF2", false, ["deriveKey"]);
  return crypto.subtle.deriveKey({ name: "PBKDF2", salt: new Uint8Array(salt), iterations: 200000, hash: "SHA-256" }, material, { name: "AES-GCM", length: 256 }, false, ["encrypt", "decrypt"]);
}

async function saveEncrypted(name: string, pin: string, state: unknown) {
  if (!/^\d{6,12}$/.test(pin)) throw new Error("Choisissez un code de 6 à 12 chiffres.");
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const key = await storageKey(pin, salt);
  const data = await crypto.subtle.encrypt({ name: "AES-GCM", iv, additionalData: new TextEncoder().encode(name) }, key, new TextEncoder().encode(JSON.stringify(state)));
  localStorage.setItem(name, JSON.stringify({ salt: base64(salt), iv: base64(iv), data: base64(data) }));
}

async function loadEncrypted(name: string, pin: string): Promise<unknown> {
  const encoded = localStorage.getItem(name);
  if (!encoded) throw new Error("Aucun manifeste préparé sur ce terminal.");
  try {
    const sealed = JSON.parse(encoded) as { salt: string; iv: string; data: string };
    const key = await storageKey(pin, bytes(sealed.salt));
    const raw = await crypto.subtle.decrypt({ name: "AES-GCM", iv: bytes(sealed.iv), additionalData: new TextEncoder().encode(name) }, key, bytes(sealed.data));
    return JSON.parse(new TextDecoder().decode(raw));
  } catch { throw new Error("Code incorrect ou manifeste endommagé."); }
}

export const savePreparation = (id: number, pin: string, identity: { device: string; secret: string }) => saveEncrypted(`transport-preparation-${id}`, pin, identity);
export async function loadPreparation(id: number, pin: string) { return await loadEncrypted(`transport-preparation-${id}`, pin) as { device: string; secret: string }; }
export const saveTerminal = (id: number, pin: string, state: TerminalState) => saveEncrypted(`transport-terminal-${id}`, pin, state);
export async function loadTerminal(id: number, pin: string): Promise<TerminalState> {
  const state = await loadEncrypted(`transport-terminal-${id}`, pin) as TerminalState;
  const manifest = await verifyEnvelope(state.envelope);
  if (manifest.departure_id !== id) throw new Error("Départ incompatible.");
  state.envelope.manifest = manifest;
  return state;
}

export async function prepareOfflineAssets() {
  if (!window.isSecureContext || !("serviceWorker" in navigator) || !navigator.locks) throw new Error("La préparation hors ligne nécessite une connexion sécurisée et un navigateur compatible.");
  const registration = await navigator.serviceWorker.register("/transport-sw.js");
  if (!registration.active) {
    const worker = registration.installing || registration.waiting;
    if (!worker) throw new Error("Préparation hors ligne indisponible.");
    await new Promise<void>((resolve, reject) => {
      const timeout = window.setTimeout(() => reject(new Error("Préparation hors ligne incomplète. Réessayez avec une connexion stable.")), 15000);
      worker.addEventListener("statechange", () => {
        if (worker.state === "activated") { window.clearTimeout(timeout); resolve(); }
        if (worker.state === "redundant") { window.clearTimeout(timeout); reject(new Error("Impossible de préparer les fichiers hors ligne.")); }
      });
    });
  }
}

export async function withTerminalLock<T>(id: number, action: () => Promise<T>): Promise<T> {
  if (!navigator.locks) throw new Error("Ce navigateur ne permet pas un contrôle hors ligne sûr. Utilisez un navigateur compatible.");
  return navigator.locks.request(`transport-terminal-${id}`, action);
}
